import { MediumFormRow, FormRow, BigFormRow } from 'framework/ui/layout';
import { Stepper, Typography } from '@mui/material';
import { ReactNode } from 'react';
import Adjust from '@mui/icons-material/Adjust';
import { ProjectToolType, ProjectToolStatus, ProjectToolLogMessage } from 'domain/projects/shared/projectToolsCreation';
import {
  BooleanRadiosGroup,
  BOOLEAN_RADIUS_GROUP_OPTIONS,
  TextInput,
  useFormContext,
  ClearAfterUnmountWrapper,
} from 'framework/form';
import { AccountNameInput } from 'domain/shared/account';
import ProjectToolsFormStep from './ProjectToolsFormStep';

interface ProjectToolsElement {
  type: ProjectToolType;
  label: ReactNode;
  form: ({ type, status }: { type: ProjectToolType; status?: ProjectToolStatus }) => ReactNode;
}

interface ProjectToolsFormStepperProps {
  toolStatuses: Record<string, ProjectToolStatus>;
  toolLogs: Record<string, ProjectToolLogMessage[]>;
}

const ProjectToolsFormStepper = ({ toolStatuses, toolLogs }: ProjectToolsFormStepperProps) => {
  const { isSubmitEnabled, onChangeCallback } = useFormContext();

  const tools: ProjectToolsElement[] = [
    {
      type: 'Redmine',
      label: 'Redmine',
      form: ({ status }) => (
        <>
          <FormRow>
            <TextInput source="identifier" label="Identifier" disabled={!isSubmitEnabled || status === 'passed'} />
            <TextInput
              source="name"
              label="Name"
              disabled={!isSubmitEnabled || status === 'passed'}
              onChangeCallback={formData => {
                onChangeCallback?.({
                  ...formData,
                  sharepointProjectCodeName: formData.name,
                  gitlabGroupProjectCodeName: formData.name,
                  teamsProjectCodeName: formData.name,
                });
              }}
            />
          </FormRow>
          <FormRow style={{ alignItems: 'center' }}>
            <TextInput
              source="parentIdentifier"
              label="Parent Identifier"
              disabled={!isSubmitEnabled || status === 'passed'}
            />
            <AccountNameInput
              tooltip="Account to create Parent Redmine project"
              source="parentIdentifier"
              label="Parent Name"
              accountNameSource="parentName"
              disabled={formData => !isSubmitEnabled || formData.parentNameExists || status === 'passed'}
              customMapResponse={account => ({
                id: account.accountId,
                text: account.accountName,
                parentName: account.accountName,
              })}
              onChangeCallback={formData => {
                onChangeCallback?.({
                  parentIdentifier: formData.parentIdentifier?.toLowerCase(),
                  parentName: formData.parentName,
                });

                if (!!formData.parentIdentifier) {
                  onChangeCallback?.({
                    gitlabGroupAccountAlias: formData.parentIdentifier,
                    gitlabGroupAccountName: `${formData.parentIdentifier} - ${formData.parentName}`,
                  });
                }
              }}
            />
          </FormRow>
        </>
      ),
    },
    {
      type: 'Teams',
      label: 'Teams',
      form: ({ status }) => (
        <>
          <MediumFormRow>
            <BooleanRadiosGroup
              options={BOOLEAN_RADIUS_GROUP_OPTIONS}
              source="isNeedToCreateTeamsGroup"
              label="Is it neccasary to create a Teams group for the project?"
              disabled={!isSubmitEnabled || status === 'passed'}
            />
          </MediumFormRow>
          <BigFormRow>
            <TextInput
              source="existedTeamsWebUrl"
              label="Link to Teams Group"
              disabled={!isSubmitEnabled || status === 'passed'}
              isVisible={data => !data.isNeedToCreateTeamsGroup}
            />
            <TextInput
              source="teamsProjectCodeName"
              label="Name"
              disabled={!isSubmitEnabled || status === 'passed'}
              isVisible={data => !!data.isNeedToCreateTeamsGroup}
            />
          </BigFormRow>
        </>
      ),
    },
    {
      type: 'Gitlab',
      label: 'Gitlab',
      form: ({ status }) => (
        <>
          <MediumFormRow>
            <BooleanRadiosGroup
              options={BOOLEAN_RADIUS_GROUP_OPTIONS}
              source="isNeedToCreateGitlabGroup"
              label="Is it neccasary to create Gitlab for the project?"
              disabled={!isSubmitEnabled || status === 'passed'}
            />
          </MediumFormRow>
          <FormRow>
            <AccountNameInput
              tooltip="Account to create Gitlab Project Group"
              source="gitlabGroupAccountAlias"
              label="Related Account"
              accountNameSource="gitlabGroupAccountName"
              disabled={!isSubmitEnabled || status === 'passed'}
              isVisible={data => !!data.isNeedToCreateGitlabGroup}
              customMapResponse={account => ({
                id: account.accountId,
                text: `${account.accountId} - ${account.accountName}`,
              })}
            />
          </FormRow>
          <TextInput
            source="gitlabGroupProjectCodeName"
            label="Name"
            disabled={!isSubmitEnabled || status === 'passed'}
            isVisible={data => !!data.isNeedToCreateGitlabGroup}
          />

          <BigFormRow>
            <TextInput
              source="existedGitlabWebUrl"
              label="Link to Gitlab Group"
              disabled={!isSubmitEnabled || status === 'passed'}
              isVisible={data => !data.isNeedToCreateGitlabGroup}
            />
          </BigFormRow>
        </>
      ),
    },
    {
      type: 'Sharepoint',
      label: 'Sharepoint',
      form: ({ status }) => (
        <>
          <MediumFormRow>
            <BooleanRadiosGroup
              options={BOOLEAN_RADIUS_GROUP_OPTIONS}
              source="isNeedToCreateSharepoint"
              label="Is it neccasary to create SharePoint Document Storage for the project"
              disabled={!isSubmitEnabled || status === 'passed'}
            />
          </MediumFormRow>
          <BigFormRow>
            <TextInput
              source="existedSharepointWebUrl"
              label="Link to SharePoint"
              disabled={!isSubmitEnabled || status === 'passed'}
              isVisible={data => !data.isNeedToCreateSharepoint}
            />
            <TextInput
              source="sharepointProjectCodeName"
              label="Name"
              disabled={!isSubmitEnabled || status === 'passed'}
              isVisible={data => !!data.isNeedToCreateSharepoint}
            />
          </BigFormRow>
        </>
      ),
    },
    {
      type: '1Password',
      label: '1Password',
      form: () => (
        <MediumFormRow>
          <ClearAfterUnmountWrapper isVisible={data => !data.isNeedToCreateOnePassword}>
            <Typography>1Password for the project has already been created</Typography>
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper isVisible={data => !!data.isNeedToCreateOnePassword}>
            <Typography>1Password for the project not found, it will be created automatically</Typography>
          </ClearAfterUnmountWrapper>
        </MediumFormRow>
      ),
    },
  ];

  const getBaseTools = () =>
    tools.map(tool => {
      const toolStatus = toolStatuses[tool.type];
      const toolMessages = toolLogs[tool.type];

      return (
        <ProjectToolsFormStep
          key={tool.type}
          status={toolStatus}
          logs={toolMessages}
          label={tool.label}
          form={tool.form({ type: tool.type, status: toolStatus })}
          expanded={true}
          active={true}
          disabled={!isSubmitEnabled}
        />
      );
    });

  const getExtraCreatedTools = () =>
    Object.entries(toolLogs)
      .filter(([key, logs]) => !tools.some(tool => tool.type === key))
      .map(([key, logs]) => {
        const toolStatus = toolStatuses[key];

        return (
          <ProjectToolsFormStep
            key={key}
            label={`Other ${key}`}
            DefaultLabelIconComponent={Adjust}
            logs={logs}
            status={toolStatus}
            expanded={true}
            active={true}
            disabled={!isSubmitEnabled}
          />
        );
      });

  return (
    <Stepper orientation="vertical" activeStep={-1} nonLinear>
      {getBaseTools()}
      {getExtraCreatedTools()}
    </Stepper>
  );
};
export default ProjectToolsFormStepper;
